import { useState } from 'react';
import { ListTodo, Plus, Trash2 } from 'lucide-react';
import type { NuevaTareaDraft, Tarea } from '../../types';
import { useTareas } from '../../hooks/useTareas';
import { nuevoUUID } from '../../lib/uuid';
import { FormularioTarea } from './FormularioTarea';
import { TarjetaTarea } from './TarjetaTarea';
import { DialogoConfirmacion } from '../ui/DialogoConfirmacion';

function hoyISO(): string {
  const ahora = new Date();
  const y = ahora.getFullYear();
  const m = String(ahora.getMonth() + 1).padStart(2, '0');
  const d = String(ahora.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function SeccionTareas() {
  const { tareasPendientes, tareasHechas, guardarTarea, alternarHecha, eliminarTarea } =
    useTareas();

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState<Tarea | null>(null);
  const [tareaAEliminar, setTareaAEliminar] = useState<Tarea | null>(null);

  const abrirNueva = () => {
    setEditando(null);
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setEditando(null);
  };

  const onGuardar = (draft: NuevaTareaDraft) => {
    const ahora = new Date().toISOString();
    const tarea: Tarea = {
      id: editando?.id ?? nuevoUUID(),
      titulo: draft.titulo,
      fechaLimite: draft.fechaLimite,
      notas: draft.notas,
      hecha: editando?.hecha ?? false,
      creada: editando?.creada ?? ahora,
      updatedAt: ahora,
      deletedAt: null,
    };
    void guardarTarea(tarea, editando === null);
    cerrarFormulario();
  };

  const confirmarEliminar = () => {
    if (tareaAEliminar) void eliminarTarea(tareaAEliminar.id);
    setTareaAEliminar(null);
  };

  const hoy = hoyISO();

  const renderTarjeta = (tarea: Tarea) => (
    <TarjetaTarea
      key={tarea.id}
      tarea={tarea}
      vencida={tarea.fechaLimite !== null && tarea.fechaLimite < hoy}
      onAlternarHecha={(t) => void alternarHecha(t)}
      onEditar={(t) => {
        setEditando(t);
        setMostrarFormulario(true);
      }}
      onEliminar={(t) => setTareaAEliminar(t)}
    />
  );

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <ListTodo className="text-violet-600" size={32} />
              Tareas por Hacer
            </h1>
            <p className="text-gray-600 mt-1">
              {tareasPendientes.length} pendientes • {tareasHechas.length} hechas
            </p>
          </div>
          <button
            onClick={abrirNueva}
            className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md"
          >
            <Plus size={20} />
            Nueva Tarea
          </button>
        </div>
      </div>

      {mostrarFormulario && (
        <FormularioTarea
          key={editando?.id ?? 'nueva'}
          editando={editando}
          onGuardar={onGuardar}
          onCancelar={cerrarFormulario}
        />
      )}

      {tareaAEliminar && (
        <DialogoConfirmacion
          icono={<Trash2 size={24} />}
          titulo="Borrar tarea"
          mensaje={`¿Borrar "${tareaAEliminar.titulo}" de la lista? Esta acción no se puede deshacer.`}
          textoConfirmar="Sí, borrar"
          iconoBotonConfirmar={<Trash2 size={18} />}
          variante="peligro"
          onConfirmar={confirmarEliminar}
          onCancelar={() => setTareaAEliminar(null)}
        />
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Pendientes ({tareasPendientes.length})
        </h2>
        {tareasPendientes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500 flex flex-col items-center gap-2">
            <ListTodo size={32} className="text-gray-300" />
            No tienes tareas pendientes
          </div>
        ) : (
          <div className="space-y-4">{tareasPendientes.map(renderTarjeta)}</div>
        )}
      </div>

      {tareasHechas.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-500 mb-4">Hechas ({tareasHechas.length})</h2>
          <div className="space-y-4">{tareasHechas.map(renderTarjeta)}</div>
        </div>
      )}
    </>
  );
}
